/**
 * Serpentine (curved banquet) table geometry.
 *
 * A serpentine is a slice of a ring: an outer arc of radius `radiusFt`, an
 * inner arc `depthFt` closer to the centre, swept through `sweepDeg`. The
 * slice starts pointing straight up from its arc centre and sweeps clockwise,
 * so the classic quarter (90°) sits in its box exactly as the canvas has
 * always drawn it — arc centre at the box's bottom-left corner.
 *
 * The item's `widthFt` × `heightFt` stays the source of truth for the box the
 * slice is drawn in. The radius comes from the width; the height then either
 * matches the circle (every serpentine sized through the Radius / Depth / Arc
 * fields) or stretches it into an elliptical arc, which is how a serpentine
 * whose Width and Height were typed separately has always looked.
 *
 * Everything that draws a serpentine or seats people at one — the canvas, the
 * chair builder, the 3D view and the PDF — asks this file, so the chairs sit
 * on the curve the table is drawn with.
 */
export const SERPENTINE_MIN_SWEEP_DEG = 15;
export const SERPENTINE_MAX_SWEEP_DEG = 180;
export const SERPENTINE_DEFAULT_SWEEP_DEG = 90;
/** The thinnest a table may be drawn, and the smallest inner radius kept. */
export const SERPENTINE_MIN_DEPTH_FT = 0.5;
const toRad = (deg) => (deg * Math.PI) / 180;
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
export function clampSerpentineSweep(deg) {
    return Math.min(SERPENTINE_MAX_SWEEP_DEG, Math.max(SERPENTINE_MIN_SWEEP_DEG, deg));
}
/** Width of the slice's box for a unit outer radius (independent of depth). */
function unitWidth(sweepDeg) {
    return sweepDeg >= 90 ? 1 : Math.sin(toRad(sweepDeg));
}
/**
 * Height of the slice's box for a unit outer radius. The top is always the
 * outer arc's start (straight up); the bottom is the lower of the two arc
 * ends, which is the inner one while the slice still points upward.
 */
function unitHeight(sweepDeg, innerRatio) {
    const endY = -Math.cos(toRad(sweepDeg)); // sin(-90° + sweep)
    return 1 + Math.max(endY, innerRatio * endY);
}
function clampDepth(depthFt, radiusFt) {
    const maxDepth = Math.max(SERPENTINE_MIN_DEPTH_FT, radiusFt - SERPENTINE_MIN_DEPTH_FT);
    return Math.min(maxDepth, Math.max(SERPENTINE_MIN_DEPTH_FT, depthFt));
}
/** Resolve a serpentine's geometry from its box and config. */
export function resolveSerpentine(widthFt, heightFt, config) {
    const cfg = (config ?? {});
    const sweepDeg = clampSerpentineSweep(finite(cfg.serpentineSweepDeg) ?? SERPENTINE_DEFAULT_SWEEP_DEG);
    const radiusFt = Math.max(0.1, widthFt / unitWidth(sweepDeg));
    // Unset depth keeps the original look: the inner arc at half the radius.
    const depthFt = clampDepth(finite(cfg.serpentineDepthFt) ?? radiusFt / 2, radiusFt);
    const innerRadiusFt = Math.max(0, radiusFt - depthFt);
    const naturalHeight = unitHeight(sweepDeg, innerRadiusFt / radiusFt) * radiusFt;
    const stretchY = naturalHeight > 0 && heightFt > 0 ? heightFt / naturalHeight : 1;
    return {
        radiusFt,
        depthFt,
        innerRadiusFt,
        sweepDeg,
        stretchY,
        centerX: 0,
        centerY: radiusFt * stretchY,
    };
}
/** The circular box (width × height) a slice of this radius, depth and sweep fills. */
export function serpentineBox(radiusFt, depthFt, sweepDeg) {
    const sweep = clampSerpentineSweep(sweepDeg);
    const radius = Math.max(0.1, radiusFt);
    const depth = clampDepth(depthFt, radius);
    return {
        widthFt: radius * unitWidth(sweep),
        heightFt: radius * unitHeight(sweep, (radius - depth) / radius),
    };
}
/** A point on a ring of radius `r` at `angleDeg` (screen angles, 0° = right, clockwise). */
function ringPoint(g, r, angleDeg) {
    const a = toRad(angleDeg);
    return {
        x: g.centerX + r * Math.cos(a),
        y: g.centerY + r * Math.sin(a) * g.stretchY,
    };
}
/**
 * The slice as a closed polygon in feet, box top-left at the origin: along
 * the outer arc, then back along the inner one.
 *
 * `insetFt` draws a slice that many feet inside the table's own edges — both
 * curves and both ends — which is what sits under a tabletop (its pedestal).
 * It never shrinks past a sliver around the middle of the band.
 */
export function serpentineOutline(widthFt, heightFt, config, insetFt = 0) {
    const g = resolveSerpentine(widthFt, heightFt, config);
    const inset = Math.max(0, Math.min(insetFt, g.depthFt * 0.4));
    const outerR = g.radiusFt - inset;
    const innerR = g.innerRadiusFt + inset;
    // The same distance off each end, as an angle on the band's middle.
    const midR = (g.radiusFt + g.innerRadiusFt) / 2;
    const endTrim = midR > 0 ? Math.min((inset / midR) * (180 / Math.PI), g.sweepDeg * 0.4) : 0;
    const start = -90 + endTrim;
    const sweep = g.sweepDeg - endTrim * 2;
    const segments = Math.max(8, Math.ceil(sweep / 3));
    const outer = [];
    const inner = [];
    for (let i = 0; i <= segments; i++) {
        const angle = start + (sweep * i) / segments;
        outer.push(ringPoint(g, outerR, angle));
        inner.push(ringPoint(g, innerR, angle));
    }
    return [...outer, ...inner.reverse()];
}
/** SVG path data for the slice, scaled by `pxPerFt`. */
export function serpentinePath(widthFt, heightFt, config, pxPerFt) {
    const points = serpentineOutline(widthFt, heightFt, config);
    const fmt = (n) => Math.round(n * pxPerFt * 100) / 100;
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${fmt(p.x)},${fmt(p.y)}`).join(' ') + ' Z';
}
export function resolveSerpentineChairSides(config) {
    const sides = config?.serpentineChairSides;
    return sides === 'inner' || sides === 'both' ? sides : 'outer';
}
/** The corners of a `size`-ft square chair centred at (x, y), turned `rotationDeg`. */
function chairCorners(chair, size) {
    const a = toRad(chair.rotation);
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    const h = size / 2;
    return [
        [-h, -h],
        [h, -h],
        [h, h],
        [-h, h],
    ].map(([dx, dy]) => ({ x: chair.x + dx * cos - dy * sin, y: chair.y + dx * sin + dy * cos }));
}
/**
 * Whether any two neighbouring chairs in a row overlap — separating-axis test
 * on the two turned squares. Corners grazing by up to 5% of a chair (an inch
 * on a 1.5 ft chair) do not count: a chair's square is its footprint, not a
 * solid block, and two slightly turned neighbours along a gentle curve always
 * graze by that much.
 */
export function chairsCollide(chairs, sizeFt) {
    for (let i = 1; i < chairs.length; i++) {
        if (chairPairOverlaps(chairs[i - 1], chairs[i], sizeFt))
            return true;
    }
    return false;
}
/**
 * Whether any two chairs of a table overlap, neighbours or not — a table's
 * last chair meets its first all the way round, and a rectangle's corner
 * chairs meet the next side's. Same test and allowance as `chairsCollide`;
 * pairs further apart than two chair diagonals are skipped unmeasured.
 */
export function anyChairsOverlap(chairs, sizeFt) {
    const reach = sizeFt * Math.SQRT2;
    for (let i = 0; i < chairs.length; i++) {
        for (let j = i + 1; j < chairs.length; j++) {
            if (Math.hypot(chairs[i].x - chairs[j].x, chairs[i].y - chairs[j].y) >= reach)
                continue;
            if (chairPairOverlaps(chairs[i], chairs[j], sizeFt))
                return true;
        }
    }
    return false;
}
/** Separating-axis test on two turned square chairs, with the 5% grazing allowance. */
function chairPairOverlaps(first, second, sizeFt) {
    const eps = sizeFt * 0.05;
    const a = chairCorners(first, sizeFt);
    const b = chairCorners(second, sizeFt);
    const axes = [first.rotation, second.rotation].flatMap((deg) => {
        const r = toRad(deg);
        return [
            { x: Math.cos(r), y: Math.sin(r) },
            { x: -Math.sin(r), y: Math.cos(r) },
        ];
    });
    const separated = axes.some((axis) => {
        const pa = a.map((p) => p.x * axis.x + p.y * axis.y);
        const pb = b.map((p) => p.x * axis.x + p.y * axis.y);
        return Math.max(...pa) <= Math.min(...pb) + eps || Math.max(...pb) <= Math.min(...pa) + eps;
    });
    return !separated;
}
/**
 * How many of `seatCount` chairs go on the inner curve when both curves are
 * seated, so that chairs on the two curves stand as nearly the same distance
 * apart as whole chairs allow.
 *
 * Each curve spaces its chairs evenly along its own length, so a curve of
 * length L with n chairs has them L / n apart; this picks the split whose two
 * spacings differ least. Sharing by length and rounding toward the outer curve
 * — what this replaced — gave a quarter ring of seven 6 outside and 1 inside,
 * 1.8 ft apart against 3 ft, where 5 and 2 are 2.2 ft against 1.5 ft.
 *
 * Both curves get at least one chair when there are two to share and the inner
 * curve has room, and the inner curve never gets more than `innerCapacity`
 * (chairs that fit along it without touching). A tie goes to the outer curve.
 */
export function splitForEvenSpacing(seatCount, outerLengthFt, innerLengthFt, innerCapacity) {
    const most = Math.min(seatCount - 1, innerCapacity);
    if (most < 1 || innerLengthFt <= 0)
        return 0;
    let best = 1;
    let bestGap = Infinity;
    for (let inner = 1; inner <= most; inner++) {
        const gap = Math.abs(outerLengthFt / (seatCount - inner) - innerLengthFt / inner);
        if (gap < bestGap - 1e-9) {
            best = inner;
            bestGap = gap;
        }
    }
    return best;
}
/**
 * Chairs around a serpentine, spaced evenly along the curve(s) and turned to
 * face the table. Positions are chair centres relative to the box centre (the
 * same frame `calculateRoundTableChairs` uses), rotation in degrees with 0 =
 * a chair above the table facing down.
 *
 * With `both`, seats are shared so the chairs on each curve stand the same
 * distance apart (`splitForEvenSpacing`). The inner curve seats only as many as
 * its room holds — none where it is too tight for a chair — and the rest go on
 * the outer curve.
 */
export function calculateSerpentineChairs(widthFt, heightFt, config, seatCount, chairSizeFt = 1.5, sides = resolveSerpentineChairSides(config)) {
    if (seatCount <= 0)
        return [];
    const g = resolveSerpentine(widthFt, heightFt, config);
    const gap = chairSizeFt * 0.7;
    const boxCenterX = widthFt / 2;
    const boxCenterY = heightFt / 2;
    /*
     * ⚠️ **Off the curve as drawn, not the circle it came from.** A box whose
     * height was typed separately stretches the arc into an ellipse, whose
     * normal is not the radius: a chair pushed out along the radius sits closer
     * at the ends than in the middle and faces past the table. So each chair is
     * set `offset` feet along the drawn edge's own normal and turned to face it,
     * and the chairs are spaced by length along that path rather than by angle.
     * On an unstretched table all three reduce to the radial placement.
     */
    const seatAt = (edgeR, offset, angleDeg) => {
        const a = toRad(angleDeg);
        const edge = ringPoint(g, edgeR, angleDeg);
        const nx = g.stretchY * Math.cos(a);
        const ny = Math.sin(a);
        const len = Math.hypot(nx, ny) || 1;
        return {
            x: edge.x + (nx / len) * offset,
            y: edge.y + (ny / len) * offset,
            normalDeg: (Math.atan2(ny, nx) * 180) / Math.PI,
        };
    };
    /** Radius of curvature of the edge of radius `edgeR` at `angleDeg`. */
    const curvatureRadius = (edgeR, angleDeg) => {
        const a = toRad(angleDeg);
        const rx = edgeR;
        const ry = edgeR * g.stretchY;
        const speed = Math.hypot(rx * Math.sin(a), ry * Math.cos(a));
        return rx * ry > 0 ? speed ** 3 / (rx * ry) : 0;
    };
    /*
     * The path the chairs sit on, sampled. ⚠️ **An inner chair needs room.** The
     * inner edge is concave, and where it bends tighter than the chair's gap —
     * the wide end of a flat, stretched table — a path set that far inside it
     * folds back over itself: chairs bunch and face nothing. Those stretches are
     * left out of the path (they add no length, so no chair lands in them).
     */
    const seatPath = (edgeR, offset) => {
        const samples = Math.max(64, Math.ceil(g.sweepDeg * 2));
        const angles = [];
        const lengths = [];
        let total = 0;
        let prev = null;
        for (let k = 0; k <= samples; k++) {
            const angle = -90 + (g.sweepDeg * k) / samples;
            const roomy = offset >= 0 || curvatureRadius(edgeR, angle) > -offset;
            const here = seatAt(edgeR, offset, angle);
            if (roomy && prev)
                total += Math.hypot(here.x - prev.x, here.y - prev.y);
            prev = roomy ? here : null;
            angles.push(angle);
            lengths.push(total);
        }
        return { edgeR, offset, angles, lengths, total };
    };
    const outerPath = seatPath(g.radiusFt, gap);
    const innerPath = seatPath(g.innerRadiusFt, -gap);
    const place = (path, n, facingIn) => {
        const { angles, lengths, total } = path;
        return Array.from({ length: n }, (_, i) => {
            const target = (total * (i + 0.5)) / n;
            // The first sample at or past the target; the step before it has length,
            // so a chair never lands in a stretch left out of the path.
            let k = 1;
            while (k < lengths.length - 1 && lengths[k] < target)
                k++;
            const span = lengths[k] - lengths[k - 1];
            const t = span > 0 ? (target - lengths[k - 1]) / span : 0;
            const seat = seatAt(path.edgeR, path.offset, angles[k - 1] + (angles[k] - angles[k - 1]) * t);
            return {
                x: seat.x - boxCenterX,
                y: seat.y - boxCenterY,
                // An outer chair faces the table's edge inward; an inner one faces out to it.
                rotation: (((seat.normalDeg + (facingIn ? 90 : 270)) % 360) + 360) % 360,
            };
        });
    };
    // As many chairs as the inner curve holds without two touching; the rest go
    // outside. ⚠️ **Measured by laying them out, not by the curve's length.** A
    // concave curve brings neighbours closer than the length between them — the
    // chord is shorter than the arc and their backs turn toward each other — so
    // a tight inner curve "long enough for two" still crushed them together.
    // A curve too tight everywhere has no path and seats nobody.
    // Only as many as could be asked for are laid out: an outer-only table asks
    // for none, and a long curve's room for dozens is never tried when four sit here.
    let innerCapacity = sides === 'outer' ? 0 : Math.min(seatCount, Math.floor(innerPath.total / chairSizeFt));
    while (innerCapacity > 1 && chairsCollide(place(innerPath, innerCapacity, false), chairSizeFt)) {
        innerCapacity--;
    }
    let innerSeats = 0;
    if (sides === 'inner') {
        innerSeats = Math.min(seatCount, innerCapacity);
    }
    else if (sides === 'both') {
        innerSeats = splitForEvenSpacing(seatCount, outerPath.total, innerPath.total, innerCapacity);
    }
    const outerSeats = seatCount - innerSeats;
    return [...place(outerPath, outerSeats, true), ...place(innerPath, innerSeats, false)];
}
//# sourceMappingURL=serpentine.js.map